import { useBusinessLocations } from "@/features/business/hooks/businessLocationQueries";
import { ApiError } from "@/core/api/apiTypes";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/shadcn/Dialog";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isValidTimezone } from "@/shared/utils/timezones";
import { Button } from "@/shared/components/shadcn/Button";
import { useBusinesses } from "../hooks/businessQueries";
import { BusinessContext } from "./BusinessContext";

interface BusinessProviderProps {
  children: ReactNode;
}

export function BusinessProvider({ children }: BusinessProviderProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { businessId, locationId } = useParams<{
    businessId: string;
    locationId: string;
  }>();

  const { data: businesses = [], isLoading: businessesLoading, error: businessesError, refetch: refetchBusinesses } =
    useBusinesses();

  const selectedBusinessId = businessId || undefined;

  const { data: locationData, isLoading: locationsLoading, error: locationsError, refetch: refetchLocations } =
    useBusinessLocations(selectedBusinessId || "");
  const locations = useMemo(
    () => locationData?.filter((entry) => entry.businessId === selectedBusinessId) ?? [],
    [locationData, selectedBusinessId]
  );

  const selectedLocationId = locationId || undefined;

  const loading = businessesLoading || locationsLoading;

  const business = useMemo(
    () => businesses.find((b) => b.id === selectedBusinessId) || undefined,
    [businesses, selectedBusinessId]
  );

  const location = useMemo(
    () => locations.find((l) => l.id === selectedLocationId && l.businessId === selectedBusinessId) || undefined,
    [locations, selectedLocationId, selectedBusinessId]
  );

  const switchBusiness = (newBusinessId: string) => {
    // Keep the same location if it exists, otherwise use first location
    const targetLocationId = selectedLocationId;
    if (targetLocationId) {
      navigate(`/b/${newBusinessId}/l/${targetLocationId}`);
    }
  };

  const switchLocation = (newLocationId: string) => {
    // Keep the same business
    if (selectedBusinessId) {
      navigate(`/b/${selectedBusinessId}/l/${newLocationId}`);
    }
  };

  // Show loading while fetching data
  const error = businessesError || locationsError;
  const accessError = [businessesError, locationsError].some(
    (cause) => cause instanceof ApiError && [401, 403, 404].includes(cause.Status)
  );
  const retainWorkspace = !!business && !!location && !accessError;
  const retry = () => {
    void refetchBusinesses();
    if (selectedBusinessId) void refetchLocations();
  };
  if (loading) {
    return <LoadingSpinner fullScreen message="Loading workspace..." />;
  }
  if (error && !retainWorkspace) {
    return (
      <div className="h-screen">
        <Page>
          <PageHeader title="Workspace" />
          <PageContent>
            <ErrorState message={error.message} onRetry={retry} />
          </PageContent>
        </Page>
      </div>
    );
  }

  const profilePath = `/b/${businessId}/l/${locationId}/profile`;
  if (business && !isValidTimezone(business.timezone) && pathname !== profilePath) {
    return (
      <Page>
        <PageHeader title="Business timezone needs attention" />
        <PageContent>
          <ErrorState message="Choose a valid business timezone in your profile before using the workspace." />
          <Button onClick={() => navigate(profilePath)}>Open business settings</Button>
        </PageContent>
      </Page>
    );
  }

  const value = {
    business,
    businessId: selectedBusinessId,
    location,
    locationId: selectedLocationId,
    businesses,
    locations,
    loading,
    workspaceReady: !error,
    switchBusiness,
    switchLocation,
  };

  return (
    <BusinessContext.Provider value={value}>
      <Dialog open={!!error}>
        <DialogContent showCloseButton={false}
          onPointerDownOutside={event => event.preventDefault()}
          onEscapeKeyDown={event => event.preventDefault()}>
          <DialogTitle>Couldn't refresh workspace</DialogTitle>
          <DialogDescription>Workspace changes are paused until its details can be verified.</DialogDescription>
          {error && <ErrorState
            message={`${error.message} Your draft has been kept. Retry to resume editing.`} onRetry={retry} />}
        </DialogContent>
      </Dialog>
      <fieldset disabled={!!error} className="contents"
        onClickCapture={event => { if (error) { event.preventDefault(); event.stopPropagation(); } }}
        onSubmitCapture={event => { if (error) { event.preventDefault(); event.stopPropagation(); } }}>
        {children}
      </fieldset>
    </BusinessContext.Provider>
  );
}
